import { useEffectOnce } from '@trmediaab/zebra-hooks';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import api from 'atg/api/API';
import HelpButton from 'shared/components/button/HelpButton';
import ErrorInfo from 'shared/components/ErrorInfo';
import CheckboxInput from 'shared/components/input/CheckboxInput';
import SelectInput from 'shared/components/input/SelectInput';
import {
  toggleJokerTemplatesVisibility,
  toggleUserTemplatesVisibility,
} from 'shared/redux/actions';
import { ui } from 'shared/redux/selectors';
import { getText } from 'shared/utils/Text';

const TemplatePicker = ({
  betTypeName,
  betKeyInfo,
  selectedTemplate,
  setSelectedTemplate,
}) => {
  const dispatch = useDispatch();
  const longBetTypeName = getText(`bet-type-long_${betTypeName.toLowerCase()}`);
  const userTemplatesVisible = useSelector(ui.userTemplatesVisible);
  const jokerTemplatesVisible = useSelector(ui.jokerTemplatesVisible);

  const [allTemplates, setAllTemplates] = useState(null);

  const [error, setError] = useState();

  const hasLoadedTemplates = allTemplates !== null;

  const visibleTemplates = allTemplates?.filter(
    template =>
      (!template.isGlobal() && userTemplatesVisible) ||
      (template.isGlobal() && jokerTemplatesVisible),
  );

  useEffectOnce(() => {
    api.loadSavedTemplates((success, templateSet) => {
      if (success) {
        const userBetType = betTypeName;
        const jokerBetType =
          IS_NO_SITE && betTypeName === 'V75' && betKeyInfo.allowsAllIn()
            ? 'GS75'
            : betTypeName;

        // Create array of templates from loaded template data,
        // filtered by current bet type
        setAllTemplates(
          Array.from(
            { length: templateSet.getNumberOfSavedTemplates() },
            (_, i) => templateSet.getSavedTemplate(i),
          ).filter(
            template =>
              (template.isGlobal() && template.getBetType() === jokerBetType) ||
              (!template.isGlobal() && template.getBetType() === userBetType),
          ),
        );
      } else {
        setError(getText('dialog-bet-config-template-error'));
        return true; // prevents default error handling (i.e. no dialog)
      }
    });
  });

  let templatePickerContent;
  if (!hasLoadedTemplates) {
    if (error) {
      // Failed to load templates
      templatePickerContent = <ErrorInfo error={{ message: error }} />;
    } else {
      // Busy loading templates
      templatePickerContent = (
        <div className="loader loader--visible">
          <div className="loader__inner">
            <div className="spinner" />
          </div>
        </div>
      );
    }
  } else {
    if (allTemplates.length === 0) {
      // No available templates
      templatePickerContent = (
        <div>
          {getText(
            'dialog-bet-config-template-select-no-templates',
            longBetTypeName,
          )}
        </div>
      );
    } else {
      // Populated TemplatePicker
      templatePickerContent = (
        <>
          {visibleTemplates.length === 0 ? (
            <div className="dialog-bet-config-no-templates-warning">
              {userTemplatesVisible
                ? getText(
                    'dialog-bet-config-template-no-user-templates',
                    longBetTypeName,
                  )
                : jokerTemplatesVisible
                  ? getText(
                      'dialog-bet-config-template-no-joker-templates',
                      longBetTypeName,
                    )
                  : getText('dialog-bet-config-template-no-visible-templates')}
            </div>
          ) : (
            <SelectInput
              className="inline-dropdown"
              value={selectedTemplate || -1}
              onChange={evt => setSelectedTemplate(evt.target.value)}
            >
              {['none', ...visibleTemplates].map(template =>
                template === 'none' ? (
                  <option key={-1} value={-1}>
                    {getText('dialog-bet-config-template-select')}
                  </option>
                ) : (
                  <option key={template.getID()} value={template.getID()}>
                    {template.getName()}
                  </option>
                ),
              )}
            </SelectInput>
          )}
          <CheckboxInput
            className="template-checkbox"
            checked={userTemplatesVisible}
            onChange={() => dispatch(toggleUserTemplatesVisibility())}
            colorFill
            labelText={getText('dialog-bet-config-template-user-filter')}
          />
          <CheckboxInput
            className="template-checkbox"
            checked={jokerTemplatesVisible}
            onChange={() => dispatch(toggleJokerTemplatesVisibility())}
            colorFill
            labelText={getText('dialog-bet-config-template-joker-filter')}
          />
        </>
      );
    }
  }

  return (
    <div className="fgrid fgrid--dialog-form fgrid-dialog-form-wide-labels">
      <div className="fgrid-cell fgrid-cell-label">
        {getText('dialog-bet-config-template-label')}
        <HelpButton
          header={getText('help-template-header')}
          body={getText('help-template-body')}
        />
      </div>
      <div className="fgrid-cell fgrid-cell-field">{templatePickerContent}</div>
    </div>
  );
};

export default TemplatePicker;
